import { IntradayChartPoint, PreparedTrade } from '../../../../models/intraday-chart.model';
import { prepareTrades } from '../../../../models/intraday-chart.utils';
import { timeToSeconds } from '../../../../components/app-clock/utils/clock.utils';
import { SessionTimeWindow, TrendChartPoint, TrendChartTick, TrendChartView } from './trend-chart.model';

/**
 * Gridlines the price axis aims for — 5 lines / 4 intervals, per the handoff. The count floats one
 * either side of it, which is what lets the axis hug the series; see `buildPriceDomain`.
 */
export const TREND_CHART_Y_TICK_COUNT = 5;

/** How far the gridline count may drift from the target to bracket the series more tightly. */
const Y_TICK_TOLERANCE = 1;

/** Band given to a series traded at a single price, as a fraction of that price. */
const FLAT_PRICE_BAND_RATIO = 0.02;

/**
 * Decimals an abbreviated reading may spend. Four is what the 48px gutter fits — `4.9806 M` — and
 * it is therefore also the axis' resolution limit; see `legiblePriceStep`.
 */
const MAX_AXIS_PRICE_DECIMALS = 4;
const MINUTE_MS = 60_000;

/** Most labels the time axis may carry. */
const MAX_X_TICKS = 4;

/**
 * Spacings the time labels may take, smallest first. Every label is a whole multiple of the chosen
 * one counted from the session open, so the axis reads round intervals, never 12:07.
 */
const X_TICK_INTERVALS_MS = [1, 5, 15, 30, 60, 120].map(minutes => minutes * MINUTE_MS);

/** The SVG is drawn in a unit box and stretched with `preserveAspectRatio="none"`. */
export const TREND_CHART_VIEW_SIZE = 100;

/** Session the chart falls back to when `/symbols/groups` returned no market groups to read one from. */
export const DEFAULT_SESSION_WINDOW: SessionTimeWindow = { start: '12:00', end: '18:00' };

const EMPTY_VIEW: TrendChartView = { linePath: '', areaPath: '', yTicks: [], xTicks: [], points: [] };

/**
 * Round factors a price step may take, per magnitude — finer than the classic 1 / 2 / 2.5 / 5
 * ladder, whose 2× jumps left a narrow domain with only one or two gridlines to label it.
 */
const PRICE_STEP_FACTORS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8];

/** Every step that could bracket `rough`, ascending, so the first one that fits is the tightest. */
function priceStepCandidates(rough: number): number[] {
	const magnitude = 10 ** Math.floor(Math.log10(rough));

	return [magnitude / 10, magnitude, magnitude * 10, magnitude * 100].flatMap(scale =>
		PRICE_STEP_FACTORS.map(factor => factor * scale)
	);
}

/** Decimals needed to render `value` exactly, so a 1.5-style step cannot round onto its neighbor. */
function exactDecimals(value: number, cap = 4): number {
	for (let decimals = 0; decimals < cap; decimals++) {
		if (Math.abs(Number(value.toFixed(decimals)) - value) <= Math.abs(value) * 1e-9) {
			return decimals;
		}
	}

	return cap;
}

/** Strips the float noise that `Math.floor(lo / step) * step` leaves on fractional steps. */
function clean(value: number): number {
	return Number(value.toPrecision(12));
}

/** The scale `formatAxisPrice` renders `value` at: its divisor and the suffix that names it. */
function abbreviation(value: number): [number, string] {
	const abs = Math.abs(value);

	return abs >= 1e9 ? [1e9, ' B'] : abs >= 1e6 ? [1e6, ' M'] : abs >= 1e3 ? [1e3, ' K'] : [1, ''];
}

/**
 * Smallest step two neighbouring gridlines can sit apart and still *read* as different prices.
 *
 * An abbreviated label carries `MAX_AXIS_PRICE_DECIMALS` decimals of its scale, so on a millions
 * axis it resolves to 100 rial and no finer. Bracketing tighter than that does not produce a
 * closer axis, it produces an unreadable one: a 4,980,860 – 4,980,870 series brackets onto a
 * 2.5 rial step, and all six gridlines then render `4.9809 M` — a reading that is not merely
 * uninformative but *wrong*, since it rounds up past both trades it is supposed to bound.
 */
function legiblePriceStep(value: number): number {
	const [divisor] = abbreviation(value);

	return divisor / 10 ** MAX_AXIS_PRICE_DECIMALS;
}

/**
 * Brackets the series between two round prices, as closely as a round gridline step allows, and
 * reports how many gridlines that took.
 *
 * Both bounds are snapped onto the step — the floor below the lowest trade, the ceiling above the
 * highest — so every gridline reads as a round price and the outermost two say how far the line
 * can reach. That floor is what forces the tick count to float: pinned to four intervals the axis
 * has to be four whole steps tall measured from a floor it did not choose, which is how a
 * 29.59 M – 29.84 M session ended up drawn on a 29.5 M – 29.9 M axis with two fifths of the plot
 * standing empty. One interval either way costs the viewer nothing — the gridlines stay evenly
 * spaced and round — and buys back most of that height.
 *
 * Candidates are walked smallest-first and the tightest domain wins, ties going to the denser grid.
 * Some slack is unavoidable, since both bounds can only land on multiples of a round step.
 */
export function buildPriceDomain(
	lo: number,
	hi: number,
	targetTicks: number
): { min: number; max: number; step: number; tickCount: number } {
	// A flat series has no range of its own — call it a 2% band centred on its price, so its line
	// lands mid-box instead of collapsing onto an edge.
	const flatBand = hi > lo ? 0 : Math.abs(hi) * FLAT_PRICE_BAND_RATIO || 1;
	// A near-flat one has a range the labels cannot resolve — a 10 rial spread on a 4.98 M price is
	// 0.0002% — so widen it to the narrowest span the axis can actually label, and let the series
	// read as the flat thing it is rather than be amplified to full height under identical
	// gridlines. The floor only bites below ~0.01% of the price; any real intraday move clears it
	// untouched and keeps its tight bracket.
	const legibleBand = legiblePriceStep(hi) * (targetTicks - 1) - (hi - lo);
	const halfBand = Math.max(flatBand, legibleBand, 0) / 2;
	// A price axis need not start at zero, but it must never suggest a negative one.
	const low = Math.max(lo - halfBand, 0);
	const high = hi + halfBand;

	const target = targetTicks - 1;
	const fewest = Math.max(target - Y_TICK_TOLERANCE, 1);
	const most = target + Y_TICK_TOLERANCE;

	let best: { min: number; max: number; step: number; tickCount: number } | undefined;

	for (const step of priceStepCandidates((high - low) / target)) {
		const min = clean(Math.floor(low / step) * step);
		const max = clean(Math.ceil(high / step) * step);
		const intervals = Math.round((max - min) / step);

		if (intervals < fewest || intervals > most) {
			continue;
		}

		// Strict, so a tie is kept by the candidate already held — the smaller step, denser grid.
		if (!best || max - min < best.max - best.min) {
			best = { min, max, step, tickCount: intervals + 1 };
		}
	}

	if (best) {
		return best;
	}

	// Unreachable for any real price — the ladder spans four magnitudes around the series' own —
	// but an axis has to come back regardless, so fall back to the target count from a round floor.
	const step = clean((high - low) / target);
	const min = clean(Math.floor(low / step) * step);

	return { min, max: clean(min + step * target), step, tickCount: targetTicks };
}

/**
 * `45000` → `45 K`. Latin digits and the K/M/B suffixes match the handoff's axis.
 *
 * `step` is the gap between neighboring ticks: a 20 K step on a 29.6 M axis needs two
 * decimals, or all five labels round to the same `29.6 M` / `29.7 M` and the axis reports
 * nothing. Omitting it keeps the single-decimal reading used for one-off figures.
 */
export function formatAxisPrice(value: number, step = 0): string {
	const [divisor, suffix] = abbreviation(value);
	const scaled = value / divisor;

	if (step <= 0) {
		return `${Math.round(scaled * 10) / 10}${suffix}`;
	}

	// Enough decimals to render the step exactly, capped so a vanishingly small one cannot run the
	// label out of its gutter. A 15 K step on a 4.7 M axis needs three: at two, 4.695 M and 4.700 M
	// both read `4.70 M`.
	const decimals = exactDecimals(step / divisor, MAX_AXIS_PRICE_DECIMALS);

	return `${scaled.toFixed(decimals)}${suffix}`;
}

export function formatTruncatedAxisPrice(value: number, step: number): string {
	const [divisor, suffix] = abbreviation(value);
	const decimals = exactDecimals(step / divisor, MAX_AXIS_PRICE_DECIMALS);
	const factor = 10 ** decimals;
	const truncated = Math.trunc((value / divisor) * factor + 1e-9) / factor;

	return `${truncated.toFixed(decimals)}${suffix}`;
}

/**
 * `1,250,000` — the readout's price, in full.
 *
 * The axis abbreviates because five labels have to fit a 48px gutter; a tooltip names one trade and
 * has the room, so it reports the price traded rather than a rounded reading of it.
 */
export function formatExactPrice(value: number): string {
	return Math.round(value)
		.toString()
		.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * `HH:mm` in exchange wall-clock time — the same reading `parseSessionTime` produces.
 *
 * The gutter has no room for seconds, and never needs it: every tick is a round reading.
 */
export function formatAxisTime(timeMs: number): string {
	const date = new Date(timeMs);

	return `${padTimePart(date.getHours())}:${padTimePart(date.getMinutes())}`;
}

/**
 * `HH:mm:ss` in exchange wall-clock time — what the readout says a selected trade happened at.
 *
 * The gutter rounds to the minute because its labels stand on round readings; a picked trade stands
 * on nothing but itself, and the readout has the room, so it names the instant to the second rather
 * than a truncation of it.
 */
export function formatReadoutTime(timeMs: number): string {
	const date = new Date(timeMs);

	return `${formatAxisTime(timeMs)}:${padTimePart(date.getSeconds())}`;
}

/** Two-digit wall-clock part — `9` reads as `09`, so every label keeps the same width. */
function padTimePart(part: number): string {
	return `${part}`.padStart(2, '0');
}

/** The handoff's gridlines: `count` round prices spanning the box, the highest at the top edge. */
function buildYTicks(min: number, step: number, count: number): TrendChartTick[] {
	// Rendered top-down, so the highest price is the first tick.
	return Array.from({ length: count }, (_, index) => {
		const value = clean(min + step * (count - 1 - index));

		return { value, label: formatAxisPrice(value, step), position: (index / (count - 1)) * 100 };
	});
}

/**
 * The chart's whole reading of a feed response: the trades it may draw, in the order it draws them.
 *
 * `prepareTrades` resolves the API's timestamps and sorts, which is what makes "first" and "last"
 * mean anything; the session filter then decides whether there is a session to draw at all. Composed here rather than at the call site so the chart has one entry point
 * from response to drawable series, and so the order cannot be got wrong somewhere else.
 *
 * `nowMs` is the reading of the clock the session is judged against — see `keepTodaysSessionTrades`.
 */
export function buildTrendChartSeries(
	points: readonly IntradayChartPoint[],
	nowMs: number,
	sessionWindow: SessionTimeWindow | null
): PreparedTrade[] {
	return sessionWindow ? keepTodaysSessionTrades(prepareTrades(points), nowMs, sessionWindow) : [];
}

/** Today's open and close, in the wall-clock frame `parseSessionTime` reads trades in. */
function sessionBounds(
	nowMs: number,
	sessionWindow: SessionTimeWindow
): { openMs: number; closeMs: number } | undefined {
	const startSeconds = timeToSeconds(sessionWindow.start);
	const endSeconds = timeToSeconds(sessionWindow.end);
	if (startSeconds === null || endSeconds === null) {
		return undefined;
	}

	const now = new Date(nowMs);
	const at = (seconds: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, seconds).getTime();

	return { openMs: at(startSeconds), closeMs: at(endSeconds) };
}

/**
 * The time axis' domain: from today's scheduled open to the current time while the market is open,
 * and to the scheduled close once it has shut. No schedule or a time before open has no domain.
 */
export function buildSessionTimeDomain(
	nowMs: number,
	sessionWindow: SessionTimeWindow | null
): { startMs: number; endMs: number } | undefined {
	if (!sessionWindow) {
		return undefined;
	}
	const bounds = sessionBounds(nowMs, sessionWindow);
	if (!bounds) {
		return undefined;
	}
	const { openMs, closeMs } = bounds;

	return nowMs < openMs ? undefined : { startMs: openMs, endMs: Math.min(nowMs, closeMs) };
}

/**
 * Keeps only trades stamped today between the scheduled open and close, both ends included.
 *
 * The endpoint is documented as the current session, but a chart of the current session must never
 * be able to render a stale one: on a holiday, or on any day the feed replays its last session,
 * the card has to reach its empty state rather than pass yesterday's line off as today's. This is
 * also what retires the chart when the trading date turns over.
 *
 * Dates are compared in the same wall-clock frame `parseSessionTime` reads its timestamps in, so a
 * device whose clock disagrees with the exchange's date will filter the session out entirely.
 */
function keepTodaysSessionTrades(
	trades: readonly PreparedTrade[],
	nowMs: number,
	sessionWindow: SessionTimeWindow
): PreparedTrade[] {
	const bounds = sessionBounds(nowMs, sessionWindow);
	if (!bounds) {
		return [];
	}
	const { openMs, closeMs } = bounds;

	return trades.filter(trade => trade.timeMs >= openMs && trade.timeMs <= closeMs);
}

/**
 * At most `MAX_X_TICKS` round time labels, evenly spaced from the session open.
 *
 * The interval is the smallest on `X_TICK_INTERVALS_MS` whose ticks — the open plus one per whole
 * interval elapsed — number `MAX_X_TICKS` or fewer. Ticks stop at `endMs`: a future time is never
 * labelled, and the end itself is not forced onto the axis — the current time is labelled only
 * when it lands exactly on a tick. Positions are read off the real time scale, so equal intervals
 * sit an equal distance apart.
 */
export function buildXTicks(startMs: number, endMs: number): TrendChartTick[] {
	const elapsedMs = Math.max(endMs - startMs, 0);
	const intervalMs =
		X_TICK_INTERVALS_MS.find(interval => Math.floor(elapsedMs / interval) + 1 <= MAX_X_TICKS) ??
		X_TICK_INTERVALS_MS[X_TICK_INTERVALS_MS.length - 1];
	const count = Math.floor(elapsedMs / intervalMs) + 1;

	return Array.from({ length: count }, (_, index) => {
		const value = startMs + index * intervalMs;
		const position = elapsedMs ? ((value - startMs) / elapsedMs) * 100 : 0;
		// The outermost labels turn inward so neither hangs outside the box.
		const align = position === 0 ? 'start' : position === 100 ? 'end' : 'center';

		return { value, label: formatAxisTime(value), position, align };
	});
}

/**
 * The plotted trade nearest `xRatio`, the horizontal position of a touch or a click as a fraction
 * of the plot box.
 *
 * Nearest by time alone: a tap is aimed with a fingertip along an axis whose whole span is a few
 * hundred pixels, so the trade under the finger is the one whose x is closest, whatever the line
 * is doing vertically. `points` is in time order, so the walk stops at the first point that is
 * further away than the one before it.
 */
export function findNearestPoint(points: readonly TrendChartPoint[], xRatio: number): TrendChartPoint | undefined {
	if (!points.length) {
		return undefined;
	}

	const x = xRatio * TREND_CHART_VIEW_SIZE;
	let nearest = points[0];

	for (const point of points) {
		if (Math.abs(point.x - x) > Math.abs(nearest.x - x)) {
			break;
		}

		nearest = point;
	}

	return nearest;
}

/**
 * Projects the session's trades onto the unit viewBox and derives both axes.
 *
 * `trades` must already be sorted and filtered by `buildTrendChartSeries`. The time axis runs
 * from the scheduled open to `nowMs`, capped at the scheduled close — see `buildSessionTimeDomain`. An
 * empty series, or a clock still short of the open, yields an empty view so the component can fall
 * back to its no-data state.
 */
export function buildTrendChartView(
	trades: readonly PreparedTrade[],
	nowMs: number,
	sessionWindow: SessionTimeWindow | null,
	targetYTicks: number = TREND_CHART_Y_TICK_COUNT,
	lastTradedPrice?: number
): TrendChartView {
	const timeDomain = buildSessionTimeDomain(nowMs, sessionWindow);

	if (!trades.length || !timeDomain) {
		return EMPTY_VIEW;
	}

	const lastPrice = lastTradedPrice && lastTradedPrice > 0 ? lastTradedPrice : undefined;
	// The live price is newer than the feed's last trade, so it stands in for that trade's price:
	// the line ends where the price actually is, and the axis bounds take it in like any trade.
	const series =
		lastPrice === undefined ? trades : [...trades.slice(0, -1), { ...trades[trades.length - 1], price: lastPrice }];
	const prices = series.map(trade => trade.price);
	const { min, max, step, tickCount } = buildPriceDomain(Math.min(...prices), Math.max(...prices), targetYTicks);
	const { startMs, endMs } = timeDomain;

	const xRange = Math.max(endMs - startMs, 1);
	const yRange = max - min || 1;
	const size = TREND_CHART_VIEW_SIZE;

	const points: TrendChartPoint[] = series.map(trade => ({
		// Clamped so a trade stamped past a lagging device clock cannot be drawn outside the box.
		x: Math.min(((trade.timeMs - startMs) / xRange) * size, size),
		y: ((max - trade.price) / yRange) * size,
		timeMs: trade.timeMs,
		price: trade.price,
		timeLabel: formatReadoutTime(trade.timeMs),
		priceLabel: formatExactPrice(trade.price)
	}));

	const polyline = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
	const first = points[0];
	const last = points[points.length - 1];

	// A lone trade has no segment to stroke, and a path that only moves paints nothing at all. Give
	// it a zero-length segment, which the round line cap renders as a visible dot.
	const linePath = points.length > 1 ? polyline : `${polyline} L ${first.x} ${first.y}`;
	const areaPath = points.length > 1 ? `${polyline} L ${last.x} ${size} L ${first.x} ${size} Z` : '';

	return {
		linePath,
		areaPath,
		yTicks: buildYTicks(min, step, tickCount),
		xTicks: buildXTicks(startMs, endMs),
		points,
		lastPriceLine:
			lastPrice === undefined
				? undefined
				: {
						position: last.y,
						end: last.x,
						label: formatTruncatedAxisPrice(lastPrice, step)
					}
	};
}
